import { motion } from 'framer-motion';
import { Diamond, X } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const BENTO_ITEMS = [
  { label: 'Bridal Ring Collection', count: '847', value: '8,499', span: 'md:col-span-2' },
  { label: 'Luxury Necklace Pack', count: '634', value: '6,999', span: '' },
  { label: 'Earrings & Studs Library', count: '523', value: '5,499', span: '' },
  { label: 'Bracelets & Bangles Kit', count: '412', value: '4,299', span: 'md:col-span-2' },
  { label: 'Watch Components', count: '389', value: '3,999', span: '' },
  { label: 'Gemstone Settings Library', count: '518', value: '3,499', span: '' },
  { label: 'Mens Collection', count: '467', value: '2,999', span: '' },
  { label: 'Export Templates & Presets', count: '210', value: '1,499', span: '' },
];

export default function ValueComparison() {
  return (
    <section data-testid="value-comparison-section" className="relative py-20 sm:py-28" style={{ background: '#FFFFFF' }}>
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 sm:px-8">
        <motion.span custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="uppercase text-xs tracking-[0.25em] font-bold text-[#c88a5e] mb-4 block font-body" data-testid="value-overline">
          <Diamond className="inline-block w-3 h-3 mr-2 -mt-0.5" />
          The Real Value
        </motion.span>

        <motion.h2 custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="font-heading font-bold text-[#111] tracking-tight mb-12" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)' }}
          data-testid="value-headline">
          What You'd Pay Separately
        </motion.h2>

        {/* Bento Grid */}
        <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-12" data-testid="value-breakdown-grid">
          {BENTO_ITEMS.map((item, i) => (
            <div key={item.label}
              className={`card-elevated p-5 transition-all duration-300 hover:shadow-lg ${item.span}`}
              data-testid={`value-item-${i}`}>
              <span className="font-body text-sm text-[#333] block mb-3">{item.label}</span>
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-xs text-[#FDC3A1]">{item.count} STLs</span>
                <span className="font-mono text-sm text-[#999] tabular-nums">₹{item.value}</span>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Price comparison */}
        <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 justify-center" data-testid="price-comparison-cards">
          <div className="card-elevated relative px-10 py-7 text-center w-full sm:w-auto" data-testid="original-price-card">
            <span className="block text-xs uppercase tracking-widest text-[#aaa] font-body mb-2">Total Real World Value</span>
            <div className="relative inline-block">
              <span className="font-heading font-bold text-[#bbb] tabular-nums" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.25rem)' }}>₹34,999</span>
              <div className="absolute top-1/2 left-[-4px] right-[-4px] h-[3px] -rotate-6" style={{ background: '#EF4444' }} />
            </div>
            <X className="absolute top-3 right-3 w-4 h-4 text-red-400/40" />
          </div>

          <div className="hidden sm:flex items-center justify-center">
            <span className="font-heading font-bold text-[#ccc] text-lg">VS</span>
          </div>

          <div className="relative px-10 py-7 text-center w-full sm:w-auto rounded-xl" data-testid="todays-price-card"
            style={{ background: 'linear-gradient(135deg, #FFF7CD, #FFFFFF)', border: '2px solid #FDC3A1', boxShadow: '0 8px 40px rgba(253,195,161,0.15)' }}>
            <span className="block text-xs uppercase tracking-widest text-[#c88a5e] font-body font-bold mb-2">Today's Price</span>
            <span className="font-heading font-black text-[#111] tabular-nums" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>₹999</span>
            <span className="block text-xs text-[#888] font-body mt-2">One-time payment &bull; Lifetime access</span>
          </div>
        </motion.div>

        <motion.p custom={4} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="text-center mt-8 font-body text-sm text-[#888]" data-testid="savings-text">
          You save <span className="text-[#c88a5e] font-bold font-mono">₹34,000</span> — that's <span className="text-[#111] font-semibold">97% off</span>
        </motion.p>
      </div>
    </section>
  );
}
